import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useT, Namespace } from "@racoon/i18n";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  SearchableSelect,
  useToast,
} from "@racoon/ui";
import { Search } from "lucide-react";
import {
  useCreateContainerMutation,
  useUpdateContainerMutation,
} from "../../../../shared/api/container/container.grpc";
import { useListContainerTypesQuery } from "../../../../shared/api/container-type/container-type.grpc";
import { useListPickupLocationsQuery } from "../../../../shared/api/pickup-locations/pickup-locations.grpc";
import { pickupLocationPolygons } from "~/features/map-view/model/pickup-geofence";
import { ContainerLocationMap } from "./container.location-map.admin";
import { CONTAINER_STATUS_LABEL_KEY } from "./secondary-status-badge.admin";
import { containerViewStatus, type ContainerViewStatus } from "../../model/container.filters";
import { ConnectProto } from "@racoon/api";
import { matchesSearch, normalizeContainerNumber, toTimestamp } from "@racoon/lib";
import { foreignOwnerName } from "~/shared/lib/holding-type";

const { ContainerStatus, ContainerTrackingEvent } = ConnectProto;
type Container = ConnectProto.Container;

/** "" is "not chosen": the create form starts empty, an unreported container stays empty. */
type ContainerStatusValue = ContainerViewStatus | "";

interface ContainerFormData {
  name: string;
  containerTypeId: string;
  locationId: string;
  status: ContainerStatusValue;
}

interface ContainerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCompanyId?: number;
  container?: Container | null;
  presetLocationId?: number;
}

const DEFAULT_CENTER: [number, number] = [43.238949, 76.889709];
const EMPTY_FORM: ContainerFormData = { name: "", containerTypeId: "", locationId: "", status: "" };

const TRACKING_EVENT_BY_STATUS: Record<
  Exclude<ContainerViewStatus, "archived">,
  ConnectProto.ContainerTrackingEvent
> = {
  delivered: ContainerTrackingEvent.DELIVERED,
  collected: ContainerTrackingEvent.COLLECTED,
};

/** Figma "Input": a 200px label column and the control filling the rest. */
const FormRow: React.FC<{
  label: React.ReactNode;
  htmlFor?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, error, children }) => (
  <div className="grid grid-cols-[200px_1fr] items-center gap-x-4 gap-y-1">
    <Label
      htmlFor={htmlFor}
      className="text-sm font-medium leading-4 tracking-[-0.2px] text-text-body"
    >
      {label}
    </Label>
    <div className="min-w-0">{children}</div>
    {error && <p className="col-start-2 text-xs text-text-error">{error}</p>}
  </div>
);

export const ContainerDialog: React.FC<ContainerDialogProps> = ({
  open,
  onOpenChange,
  selectedCompanyId,
  container,
  presetLocationId,
}) => {
  const t = useT();
  const { toast } = useToast();
  const [createContainer, { isLoading: isCreating }] = useCreateContainerMutation();
  const [updateContainer, { isLoading: isUpdating }] = useUpdateContainerMutation();
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [mapSearch, setMapSearch] = useState("");
  const isEditMode = Boolean(container);
  const isLoading = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ContainerFormData>({ defaultValues: EMPTY_FORM });

  const {
    data: containerTypesData,
    isLoading: isContainerTypesLoading,
    refetch: refetchContainerTypes,
  } = useListContainerTypesQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );

  const containerTypes = containerTypesData?.containerTypes;

  const { data: pickupLocationsResponse, isLoading: isPickupLocationsLoading } =
    useListPickupLocationsQuery(
      { serviceProviderIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
      { skip: !selectedCompanyId },
    );

  const pickupLocations = useMemo(() => {
    if (!pickupLocationsResponse?.pickupLocations) return undefined;
    return pickupLocationsResponse.pickupLocations.filter(
      (loc) => loc.status === ConnectProto.LocationStatus.ACTIVE,
    );
  }, [pickupLocationsResponse]);

  const watchLocationId = watch("locationId");

  const selectedPickupLocation = useMemo(() => {
    if (!watchLocationId || !pickupLocations) {
      return null;
    }
    return pickupLocations.find((location) => String(location.id) === watchLocationId) ?? null;
  }, [pickupLocations, watchLocationId]);

  const selectedLocationPolygons = useMemo(
    () => (selectedPickupLocation ? pickupLocationPolygons(selectedPickupLocation.geometry) : []),
    [selectedPickupLocation],
  );

  const watchContainerTypeId = watch("containerTypeId");
  const containerTypeOptions = useMemo(
    () =>
      (containerTypes ?? [])
        .filter(
          (type) =>
            type.status !== ConnectProto.ContainerTypeStatus.INACTIVE ||
            String(type.id) === watchContainerTypeId,
        )
        // Another company of the holding made it: say whose
        .map((type) => ({
          id: String(type.id),
          name: type.name,
          secondary: foreignOwnerName(type, selectedCompanyId),
        })),
    [containerTypes, watchContainerTypeId, selectedCompanyId],
  );

  const locationOptions = useMemo(
    () =>
      (pickupLocations ?? []).map((location) => ({ id: String(location.id), name: location.name })),
    [pickupLocations],
  );

  // "Archived" is only offered while editing: a container is never created archived.
  const statusOptions = useMemo(() => {
    const statuses: ContainerViewStatus[] = isEditMode
      ? ["delivered", "collected", "archived"]
      : ["delivered", "collected"];
    return statuses.map((status) => ({
      id: status,
      name: t(Namespace.Settings, CONTAINER_STATUS_LABEL_KEY[status]),
    }));
  }, [isEditMode, t]);

  // The search over the map is a second way into the site field: it matches the
  // same list the dropdown shows and writes into the same form value.
  const mapSearchResults = useMemo(() => {
    const query = mapSearch.trim();
    if (!query || !pickupLocations) return [];
    return pickupLocations.filter((location) => matchesSearch(location.name, query)).slice(0, 8);
  }, [mapSearch, pickupLocations]);

  useEffect(() => {
    if (selectedPickupLocation?.lat && selectedPickupLocation?.lon) {
      setMapCenter([selectedPickupLocation.lat, selectedPickupLocation.lon]);
    }
  }, [selectedPickupLocation]);

  // Separate effect for centering map when preset location exists
  useEffect(() => {
    if (open && presetLocationId && pickupLocations && !container) {
      const location = pickupLocations.find((loc) => loc.id === BigInt(presetLocationId));
      if (location?.lat && location.lon) {
        setMapCenter([location.lat, location.lon]);
      }
    }
  }, [open, presetLocationId, pickupLocations, container]);

  useEffect(() => {
    if (open && container) {
      // Edit mode - populate form with container data
      reset({
        name: container.name || "",
        containerTypeId: container.type?.id?.toString() || "",
        locationId: container.pickupLocationId ? container.pickupLocationId.toString() : "",
        status: containerViewStatus(container) ?? "",
      });
      // Set map center to container location
      if (container.pickupLocationId && pickupLocations) {
        const location = pickupLocations.find((loc) => loc.id === container.pickupLocationId);
        if (location?.lat && location.lon) {
          setMapCenter([location.lat, location.lon]);
        }
      }
    } else if (open && !container && presetLocationId) {
      // Create mode with preset location
      reset({ ...EMPTY_FORM, locationId: presetLocationId.toString() });
      // Map centering is handled in separate useEffect above
    } else if (!open) {
      // Modal close - reset form
      reset(EMPTY_FORM);
      setMapCenter(DEFAULT_CENTER);
      setMapSearch("");
    }
  }, [open, container, reset, pickupLocations, presetLocationId]);

  // 007, 07 and 7 all name container 7 — the number is normalized before it is
  // sent so the registry cannot end up holding one container three times.
  const onSubmit = async (rawData: ContainerFormData) => {
    const data: ContainerFormData = {
      ...rawData,
      name: normalizeContainerNumber(rawData.name),
    };

    if (!selectedCompanyId) {
      toast({
        title: t(Namespace.Settings, "inventory.dialog.companyRequired"),
        variant: "destructive",
      });
      return;
    }

    // The type list may predate an archive made elsewhere, so check the fresh one.
    // A container keeps the archived type it already has; it just can't take one on.
    // refetch resolves (never throws) with no data on failure: fall back to the cached list.
    const { data: freshTypes } = await refetchContainerTypes();
    const pickedType = (freshTypes?.containerTypes ?? containerTypes)?.find(
      (type) => String(type.id) === data.containerTypeId,
    );
    if (
      pickedType?.status === ConnectProto.ContainerTypeStatus.INACTIVE &&
      String(container?.type?.id ?? "") !== data.containerTypeId
    ) {
      setError("containerTypeId", {
        message: t(Namespace.Settings, "inventory.dialog.containerTypeArchived"),
      });
      return;
    }

    const trackingEvent =
      data.status && data.status !== "archived" ? TRACKING_EVENT_BY_STATUS[data.status] : undefined;

    try {
      if (isEditMode && container) {
        // Only a status the user actually changed is written back: re-sending
        // the current event would move `last_event_at` without the container moving.
        const statusChanged = data.status !== (containerViewStatus(container) ?? "");
        await updateContainer({
          id: container.id,
          ownerOrganizationId: BigInt(selectedCompanyId),
          name: data.name,
          typeId: BigInt(data.containerTypeId),
          ...(data.locationId && { pickupLocationId: BigInt(data.locationId) }),
          ...(statusChanged && data.status === "archived" && { status: ContainerStatus.INACTIVE }),
          ...(statusChanged &&
            trackingEvent !== undefined && {
              status: ContainerStatus.ACTIVE,
              lastEvent: trackingEvent,
              lastEventAt: toTimestamp(new Date()),
            }),
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "inventory.dialog.updateSuccess"),
        });
      } else {
        // Create mode
        await createContainer({
          name: data.name,
          typeId: BigInt(data.containerTypeId),
          ownerOrganizationId: BigInt(selectedCompanyId),
          ...(data.locationId && { pickupLocationId: BigInt(data.locationId) }),
          ...(trackingEvent !== undefined && {
            lastEvent: trackingEvent,
            lastEventAt: toTimestamp(new Date()),
          }),
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "inventory.dialog.createSuccess"),
        });
      }

      onOpenChange(false);
      reset(EMPTY_FORM);
      setMapCenter(DEFAULT_CENTER);
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "inventory.dialog.error"),
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // Archiving is a status change of its own, applied straight away: it leaves
  // the rest of the form (unsaved edits included) untouched.
  const isArchived = container?.status === ContainerStatus.INACTIVE;
  const handleArchiveToggle = async () => {
    if (!container) return;
    try {
      await updateContainer({
        id: container.id,
        ownerOrganizationId: container.ownerOrganizationId,
        status: isArchived ? ContainerStatus.ACTIVE : ContainerStatus.INACTIVE,
      }).unwrap();
      toast({
        title: t(Namespace.Settings, isArchived ? "inventory.restored" : "inventory.archived"),
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "inventory.dialog.error"),
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // isSubmitting also covers the type refetch before the save, so a double click sends once.
  const isSubmitDisabled = isLoading || isSubmitting || !selectedCompanyId;

  const mapStatusMessage = useMemo(() => {
    if (!selectedCompanyId) {
      return t(Namespace.Settings, "inventory.dialog.map.noCompany");
    }
    if (isPickupLocationsLoading) {
      return t(Namespace.Settings, "inventory.dialog.map.loading");
    }
    return undefined;
  }, [isPickupLocationsLoading, selectedCompanyId, t]);

  const pickLocationFromMapSearch = (location: ConnectProto.PickupLocation) => {
    setValue("locationId", String(location.id), { shouldDirty: true, shouldValidate: true });
    setMapSearch("");
  };

  const submitLabel = isLoading
    ? t(Namespace.Buttons, "saving")
    : isEditMode
      ? t(Namespace.Buttons, "save")
      : t(Namespace.Buttons, "add");

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="custom" className="md:!max-w-[1000px]" closeButtonTop="top-4">
        <ModalHeader className="!p-4">
          <ModalTitle className="text-xl font-bold leading-6 tracking-[-0.01em] text-text-body">
            {isEditMode
              ? t(Namespace.Settings, "inventory.dialog.editTitle")
              : t(Namespace.Settings, "inventory.dialog.createTitle")}
          </ModalTitle>
        </ModalHeader>

        <ModalBody className="!p-6">
          <form onSubmit={handleSubmit(onSubmit)} id="inventory-form">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
              <div className="flex flex-1 flex-col gap-4">
                <FormRow
                  label={t(Namespace.Settings, "inventory.dialog.name")}
                  htmlFor="name"
                  error={errors.name?.message}
                >
                  <Input
                    id="name"
                    size="sm"
                    radius="lg"
                    aria-invalid={Boolean(errors.name)}
                    {...register("name", {
                      required: t(Namespace.Settings, "inventory.dialog.nameRequired"),
                      // `required` only sees the raw value, so a number made of
                      // spaces would pass it and reach the backend empty.
                      validate: (value) =>
                        normalizeContainerNumber(value).length > 0 ||
                        t(Namespace.Settings, "inventory.dialog.nameRequired"),
                      // Normalizing on blur as well as on submit is what makes the
                      // rule visible: the field shows the number that will be saved.
                      onBlur: (event) =>
                        setValue("name", normalizeContainerNumber(event.target.value), {
                          shouldValidate: true,
                        }),
                    })}
                    placeholder={t(Namespace.Settings, "inventory.dialog.namePlaceholder")}
                  />
                </FormRow>

                <FormRow
                  label={t(Namespace.Settings, "inventory.dialog.containerType")}
                  htmlFor="container-type-select"
                  error={errors.containerTypeId?.message}
                >
                  <Controller
                    control={control}
                    name="containerTypeId"
                    rules={{
                      required: t(Namespace.Settings, "inventory.dialog.containerTypeRequired"),
                    }}
                    render={({ field }) => (
                      <SearchableSelect
                        id="container-type-select"
                        size="sm"
                        value={field.value}
                        options={containerTypeOptions}
                        onChange={field.onChange}
                        // Shows the current type while the list is still loading.
                        valueLabel={container?.type?.name}
                        loading={isContainerTypesLoading}
                        invalid={Boolean(errors.containerTypeId)}
                        placeholder={t(
                          Namespace.Settings,
                          "inventory.dialog.containerTypePlaceholder",
                        )}
                        emptyLabel={t(
                          Namespace.Settings,
                          "inventory.dialog.containerTypeNoResults",
                        )}
                        searchPlaceholder={t(Namespace.Common, "search")}
                      />
                    )}
                  />
                </FormRow>

                <FormRow
                  label={t(Namespace.Settings, "inventory.dialog.status")}
                  htmlFor="container-status-select"
                >
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <SearchableSelect
                        id="container-status-select"
                        size="sm"
                        value={field.value}
                        options={statusOptions}
                        onChange={field.onChange}
                        placeholder={t(Namespace.Settings, "inventory.dialog.statusPlaceholder")}
                        searchPlaceholder={t(Namespace.Common, "search")}
                      />
                    )}
                  />
                </FormRow>

                <FormRow
                  label={t(Namespace.Settings, "inventory.dialog.location")}
                  htmlFor="location-select"
                >
                  <Controller
                    control={control}
                    name="locationId"
                    render={({ field }) => (
                      <SearchableSelect
                        id="location-select"
                        size="sm"
                        value={field.value}
                        options={locationOptions}
                        onChange={field.onChange}
                        // No "clear" row: ContainerUpdate cannot unset the site
                        // (omitting the field leaves the current one in place).
                        loading={isPickupLocationsLoading}
                        disabled={!selectedCompanyId}
                        placeholder={t(Namespace.Settings, "inventory.dialog.locationPlaceholder")}
                        emptyLabel={t(Namespace.Settings, "inventory.dialog.locationNoResults")}
                        searchPlaceholder={t(Namespace.Common, "search")}
                      />
                    )}
                  />
                </FormRow>
              </div>

              <ContainerLocationMap
                className="h-[600px] max-h-[60vh] lg:flex-1"
                center={mapCenter}
                selectedLocation={selectedPickupLocation}
                polygons={selectedLocationPolygons}
                isLoading={isPickupLocationsLoading}
                statusMessage={mapStatusMessage}
                overlay={
                  <div className="w-56">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-icon-secondary" />
                      <Input
                        id="container-map-search"
                        size="sm"
                        radius="lg"
                        className="!pl-7 shadow-sm"
                        value={mapSearch}
                        onChange={(event) => setMapSearch(event.target.value)}
                        placeholder={t(Namespace.Common, "search")}
                        disabled={!selectedCompanyId}
                        autoComplete="off"
                      />
                    </div>
                    {mapSearch.trim() && (
                      <ul className="mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-md">
                        {mapSearchResults.length > 0 ? (
                          mapSearchResults.map((location) => (
                            <li key={String(location.id)}>
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm text-text-body hover:bg-slate-100"
                                onClick={() => pickLocationFromMapSearch(location)}
                              >
                                {location.name}
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-2 text-sm text-text-subtle">
                            {t(Namespace.Settings, "inventory.dialog.locationNoResults")}
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                }
              />
            </div>
          </form>
        </ModalBody>

        <ModalFooter className="!p-4">
          {isEditMode && (
            <Button
              type="button"
              size="sm"
              variant={isArchived ? "opacityBlueWithOutline" : "solidRed"}
              // Figma "Destructive" button: Surface/error, Subhead Medium (14/16).
              className={isArchived ? "mr-auto" : "mr-auto !text-sm !leading-4 tracking-[-0.2px]"}
              onClick={handleArchiveToggle}
              disabled={isLoading}
            >
              {isArchived ? t(Namespace.Buttons, "restore") : t(Namespace.Buttons, "archive")}
            </Button>
          )}
          <Button
            form="inventory-form"
            type="submit"
            size="sm"
            className="!bg-blue-600 !text-sm font-medium hover:!bg-blue-700"
            disabled={isSubmitDisabled}
          >
            {submitLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
